import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/motion";
import { openConsultationModal } from "../components/ConsultationModal";
import {
  Rocket, Zap, Radio, Target, BarChart3, ShieldCheck,
  Send, Users, ArrowRight, ExternalLink, CheckCircle2, Cpu, Server, Layers, ChevronRight
} from "lucide-react";

const REACH_FEATURES = [
  {
    icon: Rocket,
    title: "Omnichannel Acquisition Engine",
    desc: "Seamlessly broadcast and automate across WhatsApp Business API, enterprise Email, SMS, and high-priority push notifications from one interface.",
  },
  {
    icon: Zap,
    title: "Sub-20ms Edge Telemetry",
    desc: "Hosted on neuravolt.cloud dedicated edge clusters, delivering instantaneous click-tracking, link resolution, and zero-latency analytics.",
  },
  {
    icon: Target,
    title: "Dynamic Behavioral Segmentation",
    desc: "Automatically cluster leads based on real-time browsing behaviors, engagement velocity, and transaction intent without manual tagging.",
  },
  {
    icon: BarChart3,
    title: "Live Conversion Attribution",
    desc: "Full-funnel attribution models that track visitor discovery, touchpoint engagement, cart actions, and completed revenue.",
  },
  {
    icon: ShieldCheck,
    title: "Dedicated IP Reputation & Deliverability",
    desc: "Pre-warmed enterprise IP pools, strict SPF/DKIM/DMARC configurations, and spam-shield algorithms ensuring 99.4%+ inbox placement.",
  },
  {
    icon: Cpu,
    title: "AI-Powered Dispatch Optimization",
    desc: "Machine learning models analyze historical open rates to dispatch personalized messages at the exact optimal minute for every recipient.",
  },
];

const REACH_METRICS = [
  { value: "< 20ms", label: "Edge Delivery SLA", sub: "Global DNS & CDN latency" },
  { value: "99.99%", label: "Platform Availability", sub: "Guaranteed enterprise uptime" },
  { value: "10M+", label: "Monthly Event Capacity", sub: "Scalable bare-metal workers" },
  { value: "3.4x", label: "Average Conversion Lift", sub: "Vs traditional marketing blast tools" },
];

export default function Reach() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Reach",
    "alternateName": "Neuravolt Reach Omnichannel Engine",
    "headline": "Omnichannel Audience Acquisition Platform",
    "description": "Reach is an enterprise customer acquisition and live conversion telemetry engine operating on Neuravolt Cloud high-speed edge infrastructure.",
    "applicationCategory": "MarketingApplication, Telemetry, BusinessApplication",
    "operatingSystem": "Cloud, Web",
    "url": "https://reach.neuravolt.cloud",
    "provider": {
      "@type": "Organization",
      "name": "Neuravolt",
      "legalName": "VOLTNOVATE SOLUTIONS LLP",
      "url": "https://neuravolt.in",
    },
  };

  return (
    <Layout>
      <Seo
        title="Reach Growth Engine: Customer Acquisition & Marketing Automation Platform | NeuraVolt"
        description="Reach by NeuraVolt is a high-velocity customer acquisition automation platform and omnichannel audience broadcast engine built for modern enterprise growth."
        keywords="Reach Growth Engine, customer acquisition automation platform, omnichannel broadcast platform, audience acquisition software, lead automation engine, NeuraVolt Reach"
        path="/reach"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 sm:pb-24 px-5 sm:px-8 mesh-dark relative overflow-hidden">
        <div className="blob w-[600px] h-[600px] bg-[#FF5A1F] -top-40 -left-40 animate-aurora" style={{ opacity: 0.15 }} />
        <div className="blob w-[500px] h-[500px] bg-[#3B82F6] bottom-0 -right-32 animate-aurora" style={{ opacity: 0.18 }} />

        <div className="max-w-6xl mx-auto relative z-10 text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#FF5A1F]/15 border border-[#FF5A1F]/30 text-xs font-mono font-bold text-[#FF5A1F] uppercase tracking-wider mb-6">
              Neuravolt Growth Engine
            </span>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-sm mx-auto shadow-2xl border border-white/20 mb-8 flex items-center justify-center">
              <img
                src="/brand/reach-logo.png"
                alt="Reach - Powered by Neuravolt"
                className="w-full h-auto max-h-20 sm:max-h-24 object-contain"
              />
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <h1 className="text-3xl sm:text-5xl font-bold font-display text-white tracking-tight leading-tight max-w-4xl mx-auto mb-4">
              High-Velocity Omnichannel Customer Acquisition
            </h1>
            <p className="text-base sm:text-lg text-neutral-300 max-w-2xl mx-auto leading-relaxed mb-8">
              A sovereign audience conversion engine engineered for extreme throughput, sub-20ms edge telemetry, and enterprise deliverability.
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="https://reach.neuravolt.cloud"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 rounded-full bg-[#FF5A1F] text-white font-bold hover:bg-[#ff6d38] transition-all duration-300 shadow-xl shadow-[#FF5A1F]/25 flex items-center gap-2.5 text-sm"
              >
                <span>Launch reach.neuravolt.cloud</span>
                <ExternalLink className="w-4 h-4" />
              </a>

              <button
                onClick={() => openConsultationModal("Reach Enterprise Setup")}
                className="px-8 py-4 rounded-full bg-white/10 text-white font-bold hover:bg-white/20 transition-all duration-300 border border-white/15 flex items-center gap-2 text-sm"
              >
                <span>Book Architecture Walkthrough</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Metrics Row */}
      <section className="py-12 bg-[#0E0E10] border-y border-white/10 px-5 sm:px-8">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {REACH_METRICS.map((m, idx) => (
            <Reveal key={m.label} delay={idx * 0.05}>
              <div>
                <div className="text-3xl sm:text-4xl font-display font-extrabold text-[#FF5A1F] mb-1">
                  {m.value}
                </div>
                <div className="text-xs sm:text-sm font-semibold text-white mb-0.5">
                  {m.label}
                </div>
                <div className="text-[11px] text-neutral-400">
                  {m.sub}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[11px] font-mono font-bold tracking-widest text-[#FF5A1F] uppercase mb-3 block">
              Core Capabilities
            </span>
            <h2 className="text-2xl sm:text-4xl font-display font-bold text-[#0B0B0B] mb-4">
              Engineered for High-Converting Outbound & Inbound Journeys
            </h2>
            <p className="text-sm sm:text-base text-neutral-600 leading-relaxed">
              Standard marketing platforms throttle your throughput with artificial rate limits. Reach is architected directly on dedicated Neuravolt cloud servers for extreme scale.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {REACH_FEATURES.map((item, i) => {
              const Icon = item.icon;
              return (
                <Reveal key={item.title} delay={i * 0.05}>
                  <div className="p-7 rounded-2xl bg-neutral-50 border border-neutral-200/80 hover:border-[#FF5A1F]/30 hover:shadow-lg transition-all duration-300 h-full flex flex-col justify-between">
                    <div>
                      <div className="w-12 h-12 rounded-xl bg-[#FF5A1F]/10 border border-[#FF5A1F]/20 text-[#FF5A1F] grid place-items-center mb-5">
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className="font-display font-bold text-lg text-[#0B0B0B] mb-2.5">
                        {item.title}
                      </h3>
                      <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Infrastructure Advantage Section */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 bg-neutral-900 text-white relative overflow-hidden">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className="text-[11px] font-mono font-bold tracking-widest text-[#FF5A1F] uppercase mb-3 block">
              Infrastructure Advantage
            </span>
            <h2 className="text-2xl sm:text-4xl font-display font-bold mb-6">
              Why Reach Runs on Dedicated neuravolt.cloud Bare Metal
            </h2>
            <p className="text-neutral-300 text-sm sm:text-base leading-relaxed mb-6">
              Traditional SaaS growth platforms share server pools across thousands of tenants, resulting in traffic bottlenecks and delayed campaign deliveries during peak hours.
            </p>
            <ul className="space-y-3.5 text-sm text-neutral-200">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#FF5A1F] shrink-0 mt-0.5" />
                <span>Isolated execution pools prevent multi-tenant noisy neighbor slowdowns.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#FF5A1F] shrink-0 mt-0.5" />
                <span>Zero data reselling or audience surveillance, retaining 100% data sovereignty.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#FF5A1F] shrink-0 mt-0.5" />
                <span>Instantaneous REST and webhook sync with nORBIT, CRM, and custom backends.</span>
              </li>
            </ul>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-3xl bg-neutral-950 p-8 border border-white/10 shadow-2xl">
              <div className="flex items-center justify-between pb-5 mb-5 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs font-mono font-semibold text-neutral-300">reach.neuravolt.cloud</span>
                </div>
                <span className="text-[10px] font-mono text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded border border-emerald-800">
                  ONLINE 100%
                </span>
              </div>
              <div className="space-y-4 font-mono text-xs">
                <div className="p-3.5 rounded-xl bg-white/5 border border-white/5 flex justify-between items-center">
                  <span className="text-neutral-400">Edge Gateway Ping</span>
                  <span className="text-emerald-400 font-bold">14.2 ms</span>
                </div>
                <div className="p-3.5 rounded-xl bg-white/5 border border-white/5 flex justify-between items-center">
                  <span className="text-neutral-400">Campaign Dispatch Rate</span>
                  <span className="text-white font-bold">45,000 msgs / min</span>
                </div>
                <div className="p-3.5 rounded-xl bg-white/5 border border-white/5 flex justify-between items-center">
                  <span className="text-neutral-400">Webhook Processing SLA</span>
                  <span className="text-emerald-400 font-bold">&lt; 8 ms</span>
                </div>
                <div className="p-3.5 rounded-xl bg-white/5 border border-white/5 flex justify-between items-center">
                  <span className="text-neutral-400">Active Encryption</span>
                  <span className="text-white font-bold">TLS 1.3 / AES-256</span>
                </div>
              </div>
              <div className="mt-6 pt-5 border-t border-white/10 text-center">
                <a
                  href="https://reach.neuravolt.cloud"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3 rounded-xl bg-[#FF5A1F] hover:bg-[#ff6d38] text-white font-semibold text-xs transition-colors inline-flex items-center justify-center gap-2"
                >
                  <span>Access Platform Console</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Ecosystem Interlinking */}
      <section className="py-20 px-5 sm:px-8 bg-[#090A0F] text-white border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs font-mono font-bold text-[#FF5A1F] uppercase tracking-widest block mb-2">
              ECOSYSTEM INTEGRATION
            </span>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-white mb-2">
              How Reach Interconnects with Neuravolt Products
            </h2>
            <p className="text-neutral-400 text-xs sm:text-sm">
              Seamlessly sync campaign audiences, automate billing, and operate on dedicated cloud clusters.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-5">
            <Link
              to="/norbit"
              className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#FF5A1F]/50 hover:bg-white/[0.06] transition-all duration-300 flex flex-col justify-between group"
            >
              <div>
                <Cpu className="w-6 h-6 text-[#FF5A1F] mb-3" />
                <h3 className="font-heading font-bold text-base text-white group-hover:text-[#FF5A1F] transition-colors mb-1.5">
                  nORBIT Enterprise OS
                </h3>
                <p className="text-xs text-neutral-400 leading-relaxed mb-4">
                  Stream qualified campaign leads directly into nORBIT Pipeline CRM, quotation workflows, and client ledgers.
                </p>
              </div>
              <span className="text-xs font-semibold text-[#FF5A1F] inline-flex items-center gap-1">
                Explore nORBIT <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>

            <Link
              to="/services/cloud-infrastructure"
              className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#FF5A1F]/50 hover:bg-white/[0.06] transition-all duration-300 flex flex-col justify-between group"
            >
              <div>
                <Server className="w-6 h-6 text-[#FF5A1F] mb-3" />
                <h3 className="font-heading font-bold text-base text-white group-hover:text-[#FF5A1F] transition-colors mb-1.5">
                  neuravolt.cloud
                </h3>
                <p className="text-xs text-neutral-400 leading-relaxed mb-4">
                  Powered by dedicated high-speed bare-metal clusters delivering sub-20ms SLA click telemetry worldwide.
                </p>
              </div>
              <span className="text-xs font-semibold text-[#FF5A1F] inline-flex items-center gap-1">
                Cloud Infrastructure <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>

            <Link
              to="/services/software-development"
              className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#FF5A1F]/50 hover:bg-white/[0.06] transition-all duration-300 flex flex-col justify-between group"
            >
              <div>
                <Layers className="w-6 h-6 text-[#FF5A1F] mb-3" />
                <h3 className="font-heading font-bold text-base text-white group-hover:text-[#FF5A1F] transition-colors mb-1.5">
                  Custom Engineering
                </h3>
                <p className="text-xs text-neutral-400 leading-relaxed mb-4">
                  Integrate Reach webhooks and WhatsApp automations into your bespoke mobile apps and e-commerce platforms.
                </p>
              </div>
              <span className="text-xs font-semibold text-[#FF5A1F] inline-flex items-center gap-1">
                Software Services <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-5 sm:px-8 bg-white border-t border-neutral-200">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-4xl font-display font-bold text-[#0B0B0B] mb-4">
            Ready to Scale Your Customer Reach?
          </h2>
          <p className="text-neutral-600 text-sm sm:text-base max-w-xl mx-auto mb-8">
            Experience the speed, reliability, and precision of an enterprise growth engine built without third-party limits.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://reach.neuravolt.cloud"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3.5 rounded-full bg-[#FF5A1F] text-white font-semibold hover:bg-[#ff6d38] transition-colors text-sm inline-flex items-center gap-2"
            >
              <span>Go to reach.neuravolt.cloud</span>
              <ExternalLink className="w-4 h-4" />
            </a>
            <button
              onClick={() => openConsultationModal("Reach Architecture Consultation")}
              className="px-8 py-3.5 rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-800 font-semibold transition-colors text-sm"
            >
              Consult with Our Engineers
            </button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
